import type {
  RunAutomationDto,
  RunAutomationLaunchDto,
  RunAutomationStatus,
  TestRunDto,
} from '@tms/shared';
import { Link } from '@tanstack/react-router';
import { Bot, Download, Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Card, CardHeader } from '../../components/ui';
import { useErrorText, useFormat, useT } from '../../i18n';
import { runReportHref } from '../automation/hooks';
import { useStartRunAutomation } from './api';

const TONE: Record<RunAutomationStatus, string> = {
  pending: '',
  running: 'bg-accent-soft text-accent-fg',
  success: 'bg-pass-soft text-pass',
  failed: 'bg-fail-soft text-fail',
  cancelled: 'bg-skip-soft text-skip',
  error: 'bg-fail-soft text-fail',
};

const GOING: RunAutomationStatus[] = ['pending', 'running'];

/**
 * The run's linked autotests: which checks wait for them, the pipelines they
 * went to and what their results did to the run. Shown only for a run with
 * checks whose test case has an autotest.
 */
export const RunAutotests = ({
  projectCode,
  run,
  automation,
  canStart,
}: {
  projectCode: string;
  run: TestRunDto;
  automation: RunAutomationDto;
  canStart: boolean;
}) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const start = useStartRunAutomation(projectCode, run.id);

  const linked = new Set(automation.linkedItemIds);
  if (linked.size === 0 && automation.launches.length === 0) return null;

  const waiting = run.items.filter((item) => linked.has(item.id) && item.status === 'not_run');
  const going = automation.launches.some((launch) => GOING.includes(launch.status));
  const startable = canStart && !automation.blocked && waiting.length > 0 && !going;

  const onStart = async () => {
    try {
      await start.mutateAsync();
      toast.success(t('runs.autotests.started'));
    } catch (startError) {
      toast.error(errorText(startError, 'runs.autotests.startFailed'));
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('runs.autotests.title')}
        description={`${t('runs.autotests.linked', { count: linked.size })}. ${t('runs.autotests.hint')}`}
        action={
          startable ? (
            <Button size="sm" onClick={() => void onStart()} loading={start.isPending}>
              <Play className="size-3.5" aria-hidden="true" />
              {automation.launches.length > 0
                ? t('runs.autotests.startAgain')
                : t('runs.autotests.start')}
            </Button>
          ) : null
        }
      />
      <div className="flex flex-col gap-2 px-4 pb-4">
        {automation.blocked ? (
          <p className="text-[13px] text-ink-muted">
            {t(`runs.autotests.blocked.${automation.blocked}`)}
          </p>
        ) : null}
        {automation.launches.length > 0 ? (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {automation.launches.map((launch) => (
              <LaunchRow key={launch.id} launch={launch} />
            ))}
          </ul>
        ) : null}
        {going ? (
          <p className="text-[12px] text-ink-subtle">{t('runs.autotests.waiting')}</p>
        ) : null}
      </div>
    </Card>
  );
};

const LaunchRow = ({ launch }: { launch: RunAutomationLaunchDto }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const finished = launch.appliedAt !== null;
  // A pipeline can pass while tests in it failed: what the checks got decides.
  const shown: RunAutomationStatus =
    launch.status === 'success' && (launch.applied?.failed ?? 0) > 0 ? 'failed' : launch.status;
  return (
    <li className="flex flex-col gap-1 px-3 py-2 text-[13px]">
      <div className="flex flex-wrap items-center gap-2">
        <Bot className="size-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
        <span className="font-medium text-ink">{launch.projectName}</span>
        <Badge className={TONE[shown]}>
          {GOING.includes(launch.status) ? (
            <Loader2 className="size-3 animate-spin" aria-hidden="true" />
          ) : null}
          {t(`runs.autotests.status.${shown}`)}
        </Badge>
        {launch.pipelineId ? (
          <Link
            to="/automation/runs/$runId"
            params={{ runId: launch.pipelineId }}
            search={{ project: launch.automationProjectId }}
            className="text-accent-fg hover:underline"
          >
            {t('runs.autotests.pipeline', { id: launch.pipelineId })}
          </Link>
        ) : null}
        {launch.pipelineId && finished ? (
          <a
            href={runReportHref(launch.pipelineId, launch.automationProjectId)}
            download
            title={t('automation.runs.downloadReportHint')}
            className="inline-flex items-center gap-1 text-accent-fg hover:underline"
          >
            <Download className="size-3" aria-hidden="true" />
            {t('runs.autotests.report')}
          </a>
        ) : null}
        <span className="ml-auto text-[12px] text-ink-subtle">
          {launch.startedBy} · {relative(launch.startedAt)}
        </span>
      </div>
      <p className="text-[12px] text-ink-muted">
        {t('runs.autotests.summary', {
          classes: launch.classes,
          checks: launch.checks,
          environment: launch.environment,
        })}
      </p>
      {launch.error ? <p className="text-[12px] text-fail">{launch.error}</p> : null}
      {launch.applied ? (
        <p className="text-[12px] text-ink">
          {t('runs.autotests.applied', {
            passed: launch.applied.passed,
            failed: launch.applied.failed,
            skipped: launch.applied.skipped,
          })}
          {launch.applied.missing > 0
            ? ` · ${t('runs.autotests.missing', { count: launch.applied.missing })}`
            : ''}
          {launch.applied.kept > 0
            ? ` · ${t('runs.autotests.kept', { count: launch.applied.kept })}`
            : ''}
        </p>
      ) : null}
    </li>
  );
};
